import { useEffect, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import AuthImage from "../components/AuthImage";
import { api, formatApiError } from "../lib/api";
import { Search, ShieldPlus, User } from "lucide-react";

export default function AdminBadges() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [grantId, setGrantId] = useState("community_volunteer");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/users`, { params: { q } });
    setUsers(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { api.get(`/badges/catalog`).then((r) => setCatalog(r.data.items || [])); }, []);

  const grant = async () => {
    if (!selected) return;
    setError(""); setBusy(true);
    try {
      const { data } = await api.post(`/admin/badges/grant`, {
        user_id: selected.id, badge_id: grantId, note,
      });
      if (!data.granted) setError(data.reason || "Could not grant.");
      else { setNote(""); await load(); }
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revoke = async (uid, badgeId) => {
    await api.delete(`/admin/badges/${uid}/${badgeId}`);
    await load();
  };

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="admin-badges-page">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Admin · Badges</div>
          <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Grant &amp; revoke badges</h1>
        </div>

        <div className="csc-card p-4 flex items-center gap-2">
          <Search size={14} className="text-[#94a3b8]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search users by name, email, handle…"
            className="csc-input !py-2 flex-1"
            data-testid="admin-user-search"
          />
          <button onClick={load} className="csc-btn-ghost" data-testid="admin-search-btn">Search</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
          <section className="csc-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[rgba(0,170,255,0.18)] font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">
              Users · {users.length}
            </div>
            <ul>
              {users.length === 0 && (
                <li className="px-5 py-8 text-center text-[#94a3b8] font-mono-tech text-xs uppercase tracking-wider">No users</li>
              )}
              {users.map((u) => (
                <li key={u.id} className={`flex items-center gap-3 px-5 py-3 border-b border-[rgba(0,170,255,0.18)] last:border-none ${selected?.id === u.id ? "bg-[#00aaff]/5" : ""}`} data-testid={`admin-user-${u.id}`}>
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-[rgba(0,170,255,0.18)]">
                    <AuthImage src={u.avatar_url} alt="" className="w-full h-full object-cover" fallback={<div className="w-full h-full bg-[#0d1b2e] flex items-center justify-center"><User size={12} className="text-[#94a3b8]" /></div>} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{u.name}</div>
                    <div className="font-mono-tech text-[10px] text-[#94a3b8] truncate">@{u.handle} · {u.email} · {u.role}</div>
                    {u.badges?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {u.badges.map((b) => (
                          <button key={b} onClick={() => revoke(u.id, b)} className="font-mono-tech text-[10px] px-2 py-0.5 rounded border border-[rgba(0,170,255,0.18)] text-[#94a3b8] hover:border-red-400 hover:text-red-400" data-testid={`revoke-${u.id}-${b}`}>
                            {b} ×
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setSelected(u)} className="csc-btn-ghost !py-1.5 !px-3" data-testid={`select-user-${u.id}`}>
                    <ShieldPlus size={12} /> Select
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <aside className="csc-card p-5 space-y-3" data-testid="grant-panel">
            <h2 className="font-semibold flex items-center gap-2"><ShieldPlus size={16} className="text-[#39ff14]" /> Award badge</h2>
            {!selected ? (
              <p className="text-xs text-[#94a3b8] font-mono-tech uppercase tracking-wider">Select a user from the list</p>
            ) : (
              <>
                <div className="text-sm">To: <span className="text-white">{selected.name}</span> <span className="font-mono-tech text-[10px] text-[#94a3b8]">@{selected.handle}</span></div>
                <label className="csc-label">Badge</label>
                <select className="csc-input" value={grantId} onChange={(e) => setGrantId(e.target.value)} data-testid="grant-badge-select">
                  {catalog.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <label className="csc-label">Note (optional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} className="csc-input" data-testid="grant-note-input" placeholder="Reason for award…" />
                <button onClick={grant} disabled={busy} className="csc-btn-primary w-full" data-testid="grant-badge-btn">{busy ? "Awarding…" : "Award badge"}</button>
                {error && <div className="text-xs text-red-400" data-testid="grant-error">{error}</div>}
              </>
            )}
          </aside>
        </div>
      </div>
    </DashboardShell>
  );
}
